// src/components/Figma/Footer.tsx
import { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import accordiaLogo from "../../assets/accordia_logo512x512.png";

const routes = [
  {
    name: "Blog",
    href: "/blog",
  },
  {
    name: "FAQ & Support",
    href: "/faq",
  },
  {
    name: "Your Privacy Choices",
    href: "/your-privacy-choices",
  },
  {
    name: "Privacy Policy",
    href: "/privacy-policy",
  },
  {
    name: "Terms of Service",
    href: "/terms-of-service",
  },
];

export function Footer() {
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);

  const socialLinks = [
    {
      icon: <TiktokIcon />,
      hoverIcon: <TiktokHoverIcon />,
      href: "https://www.tiktok.com/",
    },
    {
      icon: <InstagramIcon />,
      hoverIcon: <InstagramHoverIcon />,
      href: "https://www.instagram.com",
    },
    {
      icon: <FacebookIcon />,
      hoverIcon: <FacebookHoverIcon />,
      href: "https://www.facebook.com",
    },
  ];

  return (
    <footer className="accordia-footer w-100 mt-auto">

      <Container className="py-4 py-md-5">
        <Row className="gy-4 align-items-start">
          {/* Logo + copyright */}
          <Col xs={12} lg={3} className="d-flex flex-column justify-content-between">
            <div className="d-flex align-items-center mb-3 mb-lg-4">
              <img
                src={accordiaLogo}
                alt="Accordia"
                className="img-fluid"
                style={{ maxWidth: 64, maxHeight: 64 }}
              />
            </div>
            <p className="text-muted small mb-0">
              © Accordia {new Date().getFullYear()}
            </p>
          </Col>

          {/* Content columns */}
          <Col xs={12} lg={9}>
            <Row className="gy-4">
              {/* About Us */}
              <Col xs={12} md={6} lg={5}>
                <h3 className="h5 fw-semibold mb-2">About Us</h3>
                <p className="text-muted mb-0">
                  Our mission is to restore relationships and reunite people.
                  When life gets busy and emotions run high, we provide people
                  with the tools to resolve conflicts constructively. We make it
                  accessible and affordable.
                </p>
              </Col>

              {/* Quick Links */}
              <Col xs={12} md={3} lg={3}>
                <h3 className="h5 fw-semibold mb-2">Quick Links</h3>
                <ul className="list-unstyled mb-0">
                  {routes.map((route) => (
                    <li key={route.href} className="mb-1">
                      <a
                        href={route.href}
                        className="link-dark text-decoration-none fw-medium"
                      >
                        {route.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </Col>

              {/* Social Icons */}
              <Col xs={12} md={3} lg={4}>
                <h3 className="h5 fw-semibold mb-2">Connect with Us</h3>
                <ul className="list-unstyled d-flex align-items-center gap-2 mb-0">
                  {socialLinks.map((link, index) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        onMouseEnter={() => setHoveredLink(index)}
                        onMouseLeave={() => setHoveredLink(null)}
                        className="position-relative d-inline-block text-success"
                        style={{ width: 44, height: 44 }}
                        aria-label={link.href}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            opacity: hoveredLink === index ? 0 : 1,
                            transition: "opacity 0.3s ease-in-out",
                          }}
                        >
                          {link.icon}
                        </span>
                        <span
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            opacity: hoveredLink === index ? 1 : 0,
                            transition: "opacity 0.3s ease-in-out",
                          }}
                        >
                          {link.hoverIcon}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </footer>
  );
}

/* SVG icon components stay the same (no Tailwind classes used) */

function TiktokIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.0484 44H21.9516C9.84761 44 0 34.1524 0 22.0484V21.9516C0 9.8476 9.84761 0 21.9516 0H22.0484C34.1524 0 44 9.8476 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6682 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6682 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="currentColor"
      />
      <path
        d="M32.2758 16.1236V20.1374C31.5743 20.0689 30.6628 19.9095 29.6545 19.5401C28.3379 19.0576 27.3579 18.3978 26.716 17.887V25.9995L26.6996 25.9742C26.7101 26.135 26.716 26.2988 26.716 26.4642C26.716 30.4929 23.4379 33.7724 19.4077 33.7724C15.3776 33.7724 12.0995 30.4929 12.0995 26.4642C12.0995 22.4355 15.3776 19.1544 19.4077 19.1544C19.8024 19.1544 20.1897 19.1857 20.568 19.2467V23.2025C20.2046 23.0729 19.8143 23.0029 19.4077 23.0029C17.4999 23.0029 15.9465 24.5548 15.9465 26.4642C15.9465 28.3735 17.4999 29.9254 19.4077 29.9254C21.3156 29.9254 22.869 28.372 22.869 26.4642C22.869 26.3927 22.8675 26.3212 22.8631 26.2497V10.4849H26.8739C26.8888 10.8244 26.9022 11.167 26.9171 11.5066C26.9439 12.1753 27.1822 12.8172 27.5977 13.343C28.0847 13.961 28.8041 14.6789 29.8139 15.2523C30.7596 15.787 31.6473 16.0178 32.2758 16.1266V16.1236Z"
        fill="currentColor"
      />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.0484 44H21.9516C9.84762 44 0 34.1524 0 22.0484V21.9516C0 9.8476 9.84762 0 21.9516 0H22.0484C34.1524 0 44 9.8476 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6682 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6682 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="currentColor"
      />
      <path
        d="M28.5777 9.39355H15.4238C11.7897 9.39355 8.83337 12.3499 8.83337 15.984V28.0179C8.83337 31.6519 11.7897 34.6083 15.4238 34.6083H28.5777C32.2118 34.6083 35.1681 31.6519 35.1681 28.0179V15.984C35.1681 12.3499 32.2118 9.39355 28.5777 9.39355ZM11.1583 15.984C11.1583 13.6323 13.0721 11.7184 15.4238 11.7184H28.5777C30.9294 11.7184 32.8433 13.6323 32.8433 15.984V28.0179C32.8433 30.3696 30.9294 32.2834 28.5777 32.2834H15.4238C13.0721 32.2834 11.1583 30.3696 11.1583 28.0179V15.984Z"
        fill="currentColor"
      />
      <path
        d="M22.0008 28.1295C25.3801 28.1295 28.131 25.3801 28.131 21.9993C28.131 18.6185 25.3816 15.8691 22.0008 15.8691C18.62 15.8691 15.8706 18.6185 15.8706 21.9993C15.8706 25.3801 18.62 28.1295 22.0008 28.1295ZM22.0008 18.1955C24.0993 18.1955 25.8061 19.9023 25.8061 22.0008C25.8061 24.0993 24.0993 25.8061 22.0008 25.8061C19.9023 25.8061 18.1955 24.0993 18.1955 22.0008C18.1955 19.9023 19.9023 18.1955 22.0008 18.1955Z"
        fill="currentColor"
      />
      <path
        d="M28.698 16.8595C29.6077 16.8595 30.3492 16.1193 30.3492 15.2079C30.3492 14.2964 29.6092 13.5562 28.698 13.5562C27.7867 13.5562 27.0468 14.2964 27.0468 15.2079C27.0468 16.1193 27.7867 16.8595 28.698 16.8595Z"
        fill="currentColor"
      />
    </svg>
  );
}

function TiktokHoverIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
        fill="#20883C"
      />
      <path
        d="M22.0484 44H21.9516C9.84761 44 0 34.1524 0 22.0484V21.9516C0 9.8476 9.84761 0 21.9516 0H22.0484C34.1524 0 44 9.8476 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6682 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6682 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="white"
      />
      <path
        d="M32.2758 16.1238V20.1376C31.5743 20.0691 30.6628 19.9097 29.6545 19.5403C28.3379 19.0578 27.358 18.398 26.716 17.8872V25.9997L26.6997 25.9743C26.7101 26.1352 26.716 26.299 26.716 26.4643C26.716 30.493 23.438 33.7726 19.4078 33.7726C15.3776 33.7726 12.0995 30.493 12.0995 26.4643C12.0995 22.4356 15.3776 19.1546 19.4078 19.1546C19.8025 19.1546 20.1897 19.1859 20.568 19.2469V23.2027C20.2046 23.0731 19.8144 23.0031 19.4078 23.0031C17.4999 23.0031 15.9465 24.555 15.9465 26.4643C15.9465 28.3737 17.4999 29.9256 19.4078 29.9256C21.3156 29.9256 22.869 28.3722 22.869 26.4643C22.869 26.3929 22.8676 26.3214 22.8631 26.2499V10.485H26.8739C26.8888 10.8246 26.9022 11.1672 26.9171 11.5068C26.9439 12.1755 27.1822 12.8174 27.5977 13.3431C28.0848 13.9612 28.8041 14.6791 29.8139 15.2525C30.7596 15.7872 31.6473 16.018 32.2758 16.1267V16.1238Z"
        fill="white"
      />
    </svg>
  );
}

function InstagramHoverIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
        fill="#20883C"
      />
      <path
        d="M22.0484 44H21.9516C9.84762 44 0 34.1524 0 22.0484V21.9516C0 9.8476 9.84762 0 21.9516 0H22.0484C34.1524 0 44 9.8476 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6682 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6682 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="white"
      />
      <path
        d="M28.5777 9.39337H15.4237C11.7897 9.39337 8.83334 12.3497 8.83334 15.9838V28.0177C8.83334 31.6518 11.7897 34.6081 15.4237 34.6081H28.5777C32.2117 34.6081 35.1681 31.6518 35.1681 28.0177V15.9838C35.1681 12.3497 32.2117 9.39337 28.5777 9.39337ZM11.1582 15.9838C11.1582 13.6321 13.072 11.7182 15.4237 11.7182H28.5777C30.9294 11.7182 32.8432 13.6321 32.8432 15.9838V28.0177C32.8432 30.3694 30.9294 32.2833 28.5777 32.2833H15.4237C13.072 32.2833 11.1582 30.3694 11.1582 28.0177V15.9838Z"
        fill="white"
      />
      <path
        d="M22.0008 28.1294C25.3801 28.1294 28.1309 25.3801 28.1309 21.9993C28.1309 18.6184 25.3816 15.8691 22.0008 15.8691C18.6199 15.8691 15.8706 18.6184 15.8706 21.9993C15.8706 25.3801 18.6199 28.1294 22.0008 28.1294ZM22.0008 18.1954C24.0993 18.1954 25.8061 19.9023 25.8061 22.0008C25.8061 24.0993 24.0993 25.8061 22.0008 25.8061C19.9023 25.8061 18.1955 24.0993 18.1955 22.0008C18.1955 19.9023 19.9023 18.1954 22.0008 18.1954Z"
        fill="white"
      />
      <path
        d="M28.6979 16.8595C29.6076 16.8595 30.3491 16.1193 30.3491 15.2078C30.3491 14.2963 29.6091 13.5561 28.6979 13.5561C27.7867 13.5561 27.0467 14.2963 27.0467 15.2078C27.0467 16.1193 27.7867 16.8595 28.6979 16.8595Z"
        fill="white"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.0484 44H21.9516C9.84761 44 0 34.1524 0 22.0484V21.9516C0 9.84764 9.84761 0 21.9516 0H22.0484C34.1524 0 44 9.84764 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6683 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6683 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="currentColor"
      />
      <path
        d="M24.9601 17.0666V21.697H30.6882L29.7812 27.9344H24.9601V42.3052C23.9935 42.4393 23.0046 42.5093 22.0008 42.5093C20.842 42.5093 19.7042 42.4169 18.5961 42.2382V27.9344H13.3134V21.697H18.5961V16.0315C18.5961 12.5166 21.4452 9.66602 24.9616 9.66602V9.669C24.972 9.669 24.981 9.66602 24.9914 9.66602H30.6897V15.0605H26.9663C25.8597 15.0605 24.9616 15.9585 24.9616 17.0651L24.9601 17.0666Z"
        fill="currentColor"
      />
    </svg>
  );
}

function FacebookHoverIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
    >
      <rect width="44" height="44" rx="22" fill="#20883C" />
      <path
        d="M22.0484 44H21.9516C9.84761 44 0 34.1524 0 22.0484V21.9516C0 9.84764 9.84761 0 21.9516 0H22.0484C34.1524 0 44 9.84764 44 21.9516V22.0484C44 34.1524 34.1524 44 22.0484 44ZM21.9516 1.48935C10.6682 1.48935 1.48935 10.6683 1.48935 21.9516V22.0484C1.48935 33.3318 10.6682 42.5106 21.9516 42.5106H22.0484C33.3318 42.5106 42.5106 33.3318 42.5106 22.0484V21.9516C42.5106 10.6683 33.3318 1.48935 22.0484 1.48935H21.9516Z"
        fill="white"
      />
      <path
        d="M24.9601 17.0665V21.6969H30.6882L29.7812 27.9343H24.9601V42.3051C23.9935 42.4392 23.0046 42.5092 22.0008 42.5092C20.842 42.5092 19.7042 42.4168 18.5961 42.2381V27.9343H13.3134V21.6969H18.5961V16.0314C18.5961 12.5165 21.4452 9.66592 24.9616 9.66592V9.66891C24.972 9.66891 24.981 9.66592 24.9914 9.66592H30.6897V15.0604H26.9663C25.8597 15.0604 24.9616 15.9584 24.9616 17.065L24.9601 17.0665Z"
        fill="white"
      />
    </svg>
  );
}
